/**
 * Utility to sanitize chat message content from Emma.
 * Isolates raw HTML, CSS stylesheet imports (@import), <style> blocks, and full document code blocks
 * away from the chat bubble stream so they are only rendered in the side panel viewer.
 */
export function sanitizeChatContent(content: string): string {
  if (!content) return '';

  let text = content;

  // 1. Remove raw CSS @import rules and <style>...</style> blocks that leak into text
  text = text.replace(/@import\s+url\([^)]+\);?/gi, '');
  text = text.replace(/<style[\s\S]*?<\/style>/gi, '');

  // 2. Replace raw code fences containing HTML/CSS/documents with a clean status banner card
  const codeBlockRegex = /```(?:html|markdown|md|xml|json)?\s*\n?([\s\S]*?)\n?```/gi;

  text = text.replace(codeBlockRegex, (match, blockContent) => {
    const trimmed = blockContent.trim();
    if (
      trimmed.includes('<!DOCTYPE html>') ||
      trimmed.includes('<html') ||
      trimmed.includes('<div') ||
      trimmed.includes('font-family:') ||
      trimmed.length > 200
    ) {
      return '\n\n> 📄 **Document Generated** — *View and edit the complete formatted output in the side panel.*\n\n';
    }
    return match;
  });

  // 3. Remove raw HTML document structures or HTML tags if present outside code fences
  text = text.replace(/<!DOCTYPE html>[\s\S]*/gi, '\n\n> 📄 **Document Generated** — *View and edit the complete formatted output in the side panel.*\n\n');
  text = text.replace(/<html[\s\S]*?<\/html>/gi, '\n\n> 📄 **Document Generated** — *View and edit the complete formatted output in the side panel.*\n\n');
  text = text.replace(/<(?:section|article|div|header|footer|aside|main)[\s\S]*?<\/(?:section|article|div|header|footer|aside|main)>/gi, '\n\n> 📄 **Document Generated** — *View and edit the complete formatted output in the side panel.*\n\n');
  text = text.replace(/<\/?(?:section|article|div|header|footer|aside|main|nav|p|ul|li|a|span|h[1-6])\b[^>]*>/gi, '');

  // 4. Clean up multiple blank lines
  text = text.replace(/\n{3,}/g, '\n\n').trim();

  if (!text) {
    return "I've generated your document. View and edit the complete formatted output in the side panel.";
  }

  return text;
}

/**
 * Helper to strictly isolate document markup/HTML/markdown body from conversational chat text.
 * Strips metadata, grounding scores, document retrieval logs, and pipeline_audit logs.
 */
export function isolateDocumentBody(rawText: string): string {
  if (!rawText) return '';
  let str = rawText.trim();

  // 1. Check for code fences (```html ... ``` or ```markdown ... ```)
  const codeFenceMatch = str.match(/```(?:html|markdown|md|xml|json)?\s*\n?([\s\S]*?)\n?```/i);
  if (codeFenceMatch && codeFenceMatch[1].trim()) {
    return codeFenceMatch[1].trim();
  }

  // 2. Check for HTML document structures (<!DOCTYPE html> or <html...</html>)
  const htmlMatch = str.match(/(<!DOCTYPE html>[\s\S]*|<\s*html[\s\S]*?<\s*\/html\s*>)/i);
  if (htmlMatch && htmlMatch[1].trim()) {
    return htmlMatch[1].trim();
  }

  // 3. Filter out metadata headers, grounding scores, retrieval logs, and audit log footers
  const lines = str.split('\n');
  const cleanLines = lines.filter(line => {
    const l = line.trim();
    if (l.startsWith('🚀') || l.startsWith('✨ Asset Generated') || l.startsWith('Grounding Quality Score:') || l.startsWith('Documents retrieved:')) return false;
    if (l.startsWith('👉') || l.startsWith('Every step of this workflow has been logged')) return false;
    if (l.startsWith('All checkpoints approved!')) return false;
    return true;
  });

  let isolated = cleanLines.join('\n').trim();

  // If a markdown header or section separator exists, isolate from first section
  const headerIdx = isolated.search(/(?:^|\n)(?:#|##|###|--- \[)/);
  if (headerIdx !== -1) {
    isolated = isolated.substring(headerIdx).trim();
  }

  return isolated || str;
}

/**
 * Helper to extract the complete artifact payload from a message or its metadata.
 * Inspects structured metadata sources directly (meta.artifact, msg.artifact, ui_action, statelessAsset)
 * and bypasses dumping raw chat bubble text.
 */
export function extractArtifactPayload(msg: any) {
  if (!msg) return null;
  const meta = msg.metadata || {};
  const uiAction = meta.ui_action || meta.uiAction || {};
  const uiPayload = uiAction.payload || {};
  const stateless = meta.statelessAsset || {};
  const assetObj = meta.artifact || msg.artifact || stateless.asset || meta.asset || {};

  // 1. Direct Source Inspection from structured metadata
  let content = '';

  const possibleHtml =
    meta.visual_template ||
    meta.genui_html ||
    uiAction.visual_template ||
    uiAction.genui_html ||
    uiPayload.visual_template ||
    uiPayload.genui_html ||
    (typeof assetObj === 'object' && assetObj !== null ? (assetObj.visual_template || assetObj.genui_html || assetObj.html || assetObj.markup) : '');

  if (possibleHtml && typeof possibleHtml === 'string' && possibleHtml.trim()) {
    content = possibleHtml;
  } else if (typeof meta.artifact === 'string' && meta.artifact.trim()) {
    content = meta.artifact;
  } else if (meta.artifact && typeof meta.artifact === 'object') {
    content = meta.artifact.content || meta.artifact.visual_template || meta.artifact.genui_html || meta.artifact.markup || meta.artifact.body || '';
  } else if (typeof msg.artifact === 'string' && msg.artifact.trim()) {
    content = msg.artifact;
  } else if (msg.artifact && typeof msg.artifact === 'object') {
    content = msg.artifact.content || msg.artifact.visual_template || msg.artifact.genui_html || msg.artifact.markup || msg.artifact.body || '';
  } else if (typeof uiAction.content === 'string' && uiAction.content.trim()) {
    content = uiAction.content;
  } else if (typeof uiPayload.content === 'string' && uiPayload.content.trim()) {
    content = uiPayload.content;
  } else if (typeof uiPayload.markup === 'string' && uiPayload.markup.trim()) {
    content = uiPayload.markup;
  } else if (typeof assetObj === 'string' && assetObj.trim()) {
    content = assetObj;
  } else if (typeof assetObj === 'object' && assetObj !== null) {
    content = assetObj.deep_dive || assetObj.html || assetObj.all || assetObj.body || Object.values(assetObj)[0] || '';
  }

  // Filter out summary text from content immediately if present
  if (typeof content === 'string' && (content.includes('✨ **Asset Generated') || content.includes('🚀 **Autonomous Asset Generation Complete'))) {
    content = '';
  }

  let slug =
    uiAction.slug ||
    uiPayload.slug ||
    meta.slug ||
    (meta.artifact && meta.artifact.slug) ||
    msg.slug ||
    undefined;

  let previewUrl =
    uiAction.preview_url ||
    uiAction.previewUrl ||
    uiPayload.preview_url ||
    uiPayload.previewUrl ||
    meta.preview_url ||
    meta.previewUrl ||
    (meta.artifact && (meta.artifact.preview_url || meta.artifact.previewUrl)) ||
    msg.preview_url ||
    msg.previewUrl ||
    (slug ? `/api/templates/preview/${slug}` : undefined);

  // Dynamic regex extraction from msg.content if previewUrl or slug was not directly in metadata
  const rawChatText = typeof msg.content === 'string' ? msg.content : '';
  if (!slug || !previewUrl) {
    const urlMatch = rawChatText.match(/(?:\/api)?\/templates\/preview\/([a-zA-Z0-9_-]+)/);
    if (urlMatch) {
      slug = urlMatch[1];
      previewUrl = `/api/templates/preview/${slug}`;
    } else {
      const bannerMatch = rawChatText.match(/Asset Generated for\s+([^:\*\n]+)\s*:\s*\*?\*?([^\*\n]+)\*?\*?/i);
      let partnerName = meta.partner_name || meta.partnerName || '';
      let assetTypeName = meta.asset_type || meta.assetType || meta.contentType || '';

      if (bannerMatch) {
        if (!partnerName) partnerName = bannerMatch[1].trim();
        if (!assetTypeName) assetTypeName = bannerMatch[2].trim();
      }

      const cleanPartner = partnerName.toLowerCase().replace(/[^\w-]/g, '');
      const cleanType = assetTypeName.toLowerCase().replace(/[^\w-]/g, '');

      if (cleanPartner && cleanType) {
        slug = `autonomous-${cleanType}-${cleanPartner}`.replace(/--+/g, '-');
        previewUrl = `/api/templates/preview/${slug}`;
      } else if (cleanPartner) {
        slug = `autonomous-${cleanPartner}`.replace(/--+/g, '-');
        previewUrl = `/api/templates/preview/${slug}`;
      }
    }
  }

  // 2. sessionStorage fallback if metadata was stripped on refresh
  if (!content || typeof content !== 'string' || !content.trim() || (!content.includes('<') && !previewUrl)) {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const stored = sessionStorage.getItem('emma_active_artifact');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.artifact) {
            const art = parsed.artifact;
            if (art.content && !art.content.includes('✨ **Asset Generated')) {
              if (!content || (!content.includes('<') && art.content.includes('<'))) {
                content = art.content;
              }
            }
            if (!slug && art.slug) slug = art.slug;
            if (!previewUrl && art.previewUrl) previewUrl = art.previewUrl;
          }
        }
      }
    } catch (e) {}
  }

  // Final sanity check: If content is non-HTML conversational text or summary string, wipe it if we have previewUrl
  if (typeof content === 'string' && (content.includes('✨ **Asset Generated') || (!content.includes('<') && previewUrl))) {
    content = '';
  }

  // 3. Fallback Safe-Guard: If no structured metadata exists, search for code blocks or isolate document body
  if (!content || typeof content !== 'string' || !content.trim()) {
    const rawChatText = msg.content || '';
    const codeBlockMatch = rawChatText.match(/```(?:html|markdown|md|xml|json)?\s*\n?([\s\S]*?)\n?```/i);
    if (codeBlockMatch && codeBlockMatch[1].trim()) {
      content = codeBlockMatch[1].trim();
    } else {
      const isolated = isolateDocumentBody(rawChatText);
      if (isolated && isolated !== rawChatText) {
        content = isolated;
      } else {
        content = rawChatText;
      }
    }
  }

  // Clean outer code block fences if content is wrapped in one
  if (typeof content === 'string' && content.startsWith('```')) {
    const match = content.match(/^```(?:html|markdown|md|xml|json)?\s*\n?([\s\S]*?)\n?```$/i);
    if (match && match[1].trim()) {
      content = match[1].trim();
    }
  }

  // 3. Extract title, contentType, and artifactId from structured metadata
  const title =
    uiAction.title ||
    uiPayload.title ||
    meta.title ||
    (meta.artifact && meta.artifact.title) ||
    (meta.assetType ? `Generated ${String(meta.assetType).replace('_', ' ').toUpperCase()}` : 'Generated Artifact');

  const contentType =
    uiAction.contentType ||
    uiPayload.contentType ||
    meta.assetType ||
    meta.contentType ||
    (meta.artifact && meta.artifact.contentType) ||
    'document';

  const artifactId =
    uiAction.artifactId ||
    uiPayload.artifactId ||
    meta.artifactId ||
    (meta.artifact && meta.artifact.id) ||
    msg.id ||
    `art_${Date.now()}`;

  return {
    action: 'OPEN_ARTIFACT_PANEL',
    artifactId,
    title,
    contentType,
    content,
    slug,
    previewUrl,
    version: uiAction.version || meta.version || 1,
    uiAction: Object.keys(uiAction).length > 0 ? uiAction : undefined
  };
}
